/**
 * Sysadmin Administrators → Activity (Phase 5 PR-E).
 *
 * Per the Phase 5 architect plan §2.6 + OQ-2 ("no duplicate audit
 * view"): this sub-tab does NOT render its own audit table. The
 * canonical per-admin pivot lives at /sysadmin/audit/sysadmin which
 * already accepts a `sysadminId` querystring filter. We redirect here
 * so deep links from the Administrators rail continue to work while
 * the actual audit surface stays in one place.
 *
 * Redirect rules:
 *   - URL carries ?actor=<uuid>  → redirect to
 *     /sysadmin/audit/sysadmin?sysadminId=<uuid>
 *     (canonical filter key is `sysadminId`; OQ-2 specified `actor`
 *     externally — we accept the external name + map to the internal
 *     filter so spec docs and audit-tab code can stay decoupled).
 *   - No `actor`               → redirect to /sysadmin/audit/sysadmin
 *     (full sysadmin-actions firehose)
 *
 * Why a route file at all (vs. just removing the path):
 *   - The Administrators parent layout (administrators.tsx) still
 *     renders this sub-tab in the rail nav. If the file goes away,
 *     the parent's <Link> 404s. Keeping a file + redirect preserves
 *     the click-through.
 *   - Existing bookmarks / external docs pointing at the v0 stub URL
 *     continue to land somewhere useful.
 *
 * Why `beforeLoad` + `throw redirect()`:
 *   - The library/wiki/new redirect (issue #236 in round 6) proved
 *     that component-based `<Navigate>` fires AFTER the destination
 *     mounts, so the destination's `useEffect` reading
 *     `window.location.search` sees the OLD URL. `beforeLoad` runs
 *     during route resolution before the destination component
 *     mounts, so the URL is updated synchronously. We follow the
 *     same pattern here for consistency with that fix.
 *   - The destination /sysadmin/audit/sysadmin doesn't declare a
 *     typed `validateSearch`, so we use `href` (raw URL) instead of
 *     `{ to, search }` — `href` is untyped + works regardless of
 *     destination schema.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';

// Search-param shape — `actor` is the externally-documented name from
// the spec OQ-2. We accept it on the URL and translate it to the
// canonical `sysadminId` filter used by the audit module's hook.
type ActivitySearch = { actor?: string };

export const Route = createFileRoute('/sysadmin/administrators/activity')({
  // TanStack Router validates search params at the route boundary so
  // a garbage `actor=` value never reaches beforeLoad. The schema is
  // intentionally permissive (any string) — the audit page itself
  // re-validates the UUID server-side.
  validateSearch: (search: Record<string, unknown>): ActivitySearch => {
    const actor = typeof search.actor === 'string' ? search.actor : undefined;
    return { actor };
  },

  beforeLoad: ({ search }) => {
    // Single point of mapping: external `actor` → internal
    // `sysadminId`. If audit-tab ever changes its filter key, this
    // is the one place that needs to follow.
    const target = search.actor
      ? `/sysadmin/audit/sysadmin?sysadminId=${encodeURIComponent(search.actor)}`
      : '/sysadmin/audit/sysadmin';

    throw redirect({ href: target, replace: true });
  },
});
